/**
 * The Acquisition campaigns browser pool, sourced from the Casino Ad Service.
 *
 * Lists the active brand's campaigns and maps each onto the browser-card `Campaign`
 * via {@link toCampaign}. The card image is the list row's `master_key_art`, so no
 * per-campaign detail fetch is needed. The browser derives its tab counts and filtering
 * from this pool; the tabs/filters chrome stays static.
 *
 * The Running, Paused and Ended tabs are fed from here: `Running` / `Paused` map to
 * themselves, and one whose `end_date` has passed resolves to ended in the browser
 * (`utils/campaign-status`). Anything else, including a campaign the provider is still
 * building (`Starting`), is left out of the pool entirely, so it reaches no tab, count
 * or card. **The Suggested tab therefore stays empty**: this service reports campaigns
 * that already exist, not suggestions, and the tab shows its own empty state rather
 * than anything invented. Reads live; the test tenant returns the one NeonJack
 * campaign. Capped at 100 campaigns (the API page max); fine pre-proxy.
 *
 * Contract: `docs/api.owners-hub.acquisition-campaigns.md`.
 */
import React from 'react';

import type { Campaign } from '../../mock-data/types';
import { listAdCampaigns } from './api/casino-ad-service';
import { toCampaignPool } from './campaign-mapping';

export type AcquisitionCampaignsResult = { pool: Campaign[]; loading: boolean };

/**
 * @param tenantId The active brand's tenant id (`displayBrand?.tenant_id`);
 *        `undefined` keeps the browser loading (empty pool) until a brand resolves.
 */
export const useAcquisitionCampaigns = (tenantId: string | undefined): AcquisitionCampaignsResult => {
  const [pool, setPool] = React.useState<Campaign[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!tenantId) {
      setLoading(true);
      setPool([]);
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);

    listAdCampaigns({ tenantId, pageSize: 100 }, signal)
      .then((list) => {
        // The card image is the list row's master_key_art, so the pool maps straight
        // from the list — no per-campaign detail fetch. Campaigns the studio never shows
        // (Failed / Deleted / an unrecognised state) are dropped by the mapper.
        if (!signal.aborted) {
          setPool(toCampaignPool(list.campaigns));
          setLoading(false);
        }
      })
      .catch(() => {
        // Ignore the abort the cleanup triggers; a real failure shows the empty state.
        if (!signal.aborted) {
          setPool([]);
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [tenantId]);

  return { pool, loading };
};

export default useAcquisitionCampaigns;
